import { googleMapsConfig } from '../../../services/google-maps/config.js';
import { checkLocation } from '../../../services/google-maps/location-api.js';
import { loadGoogleMaps } from '../../../services/google-maps/loader.js';
import { emptySelection, hasValidCoordinates, toLatLngLiteral } from './selection.js';

const DEFAULT_CENTER = { lat: 26.4207, lng: 50.0888 };
const DEFAULT_ZOOM = 12;
const FOCUSED_ZOOM = 15;
const GEOLOCATION_TIMEOUT_MS = 10000;

// Rough bounding box covering the Middle East / North Africa, keeps the map from drifting away.
const MAP_RESTRICTION_BOUNDS = { north: 38, south: 5, west: 25, east: 65 };

/**
 * Map + search UI for picking a point. It only collects coordinates: whether the point is inside
 * the service area is decided by the backend (check-location API), and saving is validated again
 * server side by ValidLocationRule.
 *
 * Expects `x-ref="mapContainer"` and, optionally, `x-ref="searchContainer"` for the place search.
 * Every pick dispatches a `location-selected` browser event with the `selected` payload.
 *
 * @param {{ initialLocation?: { lat: number, lng: number, address?: string }|null, model?: string|null }} config
 *        `initialLocation` pre-fills edit mode; `model` is the Livewire property to sync the pick into
 *        (e.g. `location` or `form.location`).
 */
export default function locationPicker({ initialLocation = null, model = null } = {}) {
    // Google objects stay outside Alpine's reactive state: proxying them breaks the Maps API.
    let map = null;
    let marker = null;
    let autocomplete = null;
    let pendingCheck = null;

    return {
        isLoading: true,
        isLocating: false,
        isChecking: false,
        hasError: false,
        selected: emptySelection(),

        async init() {
            try {
                const { Map, AdvancedMarkerElement, PlaceAutocompleteElement } = await loadGoogleMaps();
                const { mapId, allowedCountries } = googleMapsConfig();

                map = new Map(this.$refs.mapContainer, {
                    mapId,
                    center: DEFAULT_CENTER,
                    zoom: DEFAULT_ZOOM,
                    minZoom: 5,
                    disableDefaultUI: true,
                    zoomControl: true,
                    gestureHandling: 'greedy',
                    restriction: { latLngBounds: MAP_RESTRICTION_BOUNDS, strictBounds: false },
                });
                map.addListener('click', (event) => this.select(toLatLngLiteral(event.latLng)));

                marker = new AdvancedMarkerElement({ gmpDraggable: true });
                marker.addEventListener('gmp-dragend', () => this.select(toLatLngLiteral(marker.position)));

                if (this.$refs.searchContainer) {
                    autocomplete = new PlaceAutocompleteElement({
                        includedRegionCodes: allowedCountries,
                        placeholder: this.$refs.searchContainer.dataset.placeholder ?? '',
                    });
                    autocomplete.setAttribute('no-input-icon', '');
                    autocomplete.addEventListener('gmp-select', ({ placePrediction }) =>
                        this.selectPlace(placePrediction)
                    );
                    this.$refs.searchContainer.append(autocomplete);
                }

                if (hasValidCoordinates(initialLocation)) {
                    await this.select(toLatLngLiteral(initialLocation), initialLocation.address ?? null, {
                        focus: true,
                        syncModel: false,
                    });
                }
            } catch (error) {
                console.error(error);
                this.hasError = true;
            } finally {
                this.isLoading = false;
            }
        },

        destroy() {
            pendingCheck?.abort();

            if (marker) {
                marker.map = null;
            }

            if (map) {
                google.maps.event.clearInstanceListeners(map);
            }

            autocomplete?.remove();
            map = marker = autocomplete = pendingCheck = null;
        },

        /**
         * Single entry point for every way of picking a point (map click, marker drag, search,
         * geolocation, initial location).
         */
        async select({ lat, lng }, addressHint = null, { focus = false, syncModel = true } = {}) {
            marker.position = { lat, lng };
            marker.map = map;

            if (focus) {
                map.setCenter({ lat, lng });
                map.setZoom(FOCUSED_ZOOM);
            }

            pendingCheck?.abort();
            const check = new AbortController();
            pendingCheck = check;
            this.isChecking = true;
            this.hasError = false;

            try {
                const result = await checkLocation(
                    googleMapsConfig().checkLocationUrl,
                    { lat, lng, address: addressHint },
                    check.signal
                );
                this.selected = { lat, lng, ...result };
            } catch (error) {
                if (error.name === 'AbortError') {
                    return;
                }

                console.error(error);
                this.hasError = true;
                this.selected = { ...emptySelection(), lat, lng, address: addressHint };

                return;
            } finally {
                if (pendingCheck === check) {
                    pendingCheck = null;
                    this.isChecking = false;
                }
            }

            this.$dispatch('location-selected', { ...this.selected });

            if (syncModel && model && this.$wire) {
                const { address, city } = this.selected;

                this.$wire.set(model, { lat, lng, address, city });
            }
        },

        async selectPlace(placePrediction) {
            try {
                const place = placePrediction.toPlace();
                await place.fetchFields({ fields: ['location', 'formattedAddress'] });

                if (place.location) {
                    await this.select(toLatLngLiteral(place.location), place.formattedAddress ?? null, { focus: true });
                }
            } catch (error) {
                console.error(error);
                this.hasError = true;
            }
        },

        getCurrentLocation() {
            if (!navigator.geolocation) {
                this.hasError = true;

                return;
            }

            this.isLocating = true;

            navigator.geolocation.getCurrentPosition(
                ({ coords }) => {
                    this.isLocating = false;
                    this.select({ lat: coords.latitude, lng: coords.longitude }, null, { focus: true });
                },
                () => {
                    this.isLocating = false;
                    this.hasError = true;
                },
                { enableHighAccuracy: true, timeout: GEOLOCATION_TIMEOUT_MS }
            );
        },

        reset() {
            pendingCheck?.abort();
            this.selected = emptySelection();
            this.hasError = false;

            if (marker) {
                marker.map = null;
            }

            if (autocomplete) {
                autocomplete.value = '';
            }
        },
    };
}
